import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type Rule } from '../services/types';

interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage?: string; // Optional message from the server
  data?: Rule; // The actual data returned by the API
  error?: string | null; // Error details in case of failure
  status?: number;
  ok?: boolean;
}

/**
 * Sends a request to delete a rule via the external API service.
 *
 * @async
 * @function removeRule
 * @param {Rule} rule - The rule object to be deleted.
 * @returns {Promise<Rule>} The deleted rule object returned from the API.
 */
const removeRule = async (rule: Rule) => {
  const response = (await ExternalService.deleteRule(rule)) as ApiResponse;

  return response;
};

/**
 * Custom hook to handle the deletion of rules using React Query's `useMutation`.
 *
 * @function useDeleteRule
 * @returns {import('@tanstack/react-query').UseMutationResult<Rule, unknown, Rule, unknown>} A mutation result object containing mutation methods and state.
 */
const useDeleteRule = () =>
  useMutation({
    mutationFn: (rule: Rule) => removeRule(rule), // Function to fetch data
  });

export default useDeleteRule;
